import { useState, useEffect, useCallback } from 'react';
import { musician } from '@/mocks/musician';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function Gallery() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  useScrollAnimation();

  const images = musician.galleryImages;

  const handleImageLoad = useCallback((index: number) => {
    setLoadedImages((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, []);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    document.body.style.overflow = '';
  }, []);

  const goNext = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (lightboxIndex !== null && lightboxIndex < images.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  }, [lightboxIndex, images.length]);

  const goPrev = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (lightboxIndex !== null && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    }
  }, [lightboxIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev !== null && prev < images.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, closeLightbox, images.length]);

  return (
    <>
      <title>Gallery | Jackson Lacefield | Tenor &amp; Worship Musician</title>
      <meta name="description" content="A visual collection of Jackson Lacefield's performances, portraits, and moments in worship and music. Tenor and multi-instrumentalist based in Dallas / Ft. Worth and Waco." />
      <meta name="keywords" content="Jackson Lacefield, gallery, photos, performances, tenor, worship musician, portraits" />
      <link rel="canonical" href="https://www.jacksonlacefield.com/gallery" />
      <meta property="og:title" content="Gallery | Jackson Lacefield | Tenor &amp; Worship Musician" />
      <meta property="og:description" content="A visual collection of Jackson Lacefield's performances, portraits, and moments in worship and music." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.jacksonlacefield.com/gallery" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Gallery | Jackson Lacefield" />
      <meta name="twitter:description" content="A visual collection of Jackson Lacefield's performances and portraits." />
      <main className="min-h-screen bg-background-50">
      {/* ── Page Header ── */}
      <section className="page-header gradient-section-orange film-grain">
        <div className="section-container text-center relative z-10">
          <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-4 scroll-animate">
            Gallery
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground-900 mb-3 scroll-animate">
            Moments &amp; Memories
          </h1>
          <p className="font-body text-sm md:text-base text-foreground-500 max-w-lg mx-auto scroll-animate">
            A visual collection of performances, spaces, and moments that shape my journey.
          </p>
        </div>
      </section>

      {/* ── Lightbox ── */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-foreground-950/95 flex items-center justify-center p-4 md:p-8"
          onClick={closeLightbox}
        >
          {lightboxIndex > 0 && (
            <button
              onClick={goPrev}
              className="lightbox-arrow left-4 md:left-6 z-10"
              aria-label="Previous image"
            >
              <i className="ri-arrow-left-line text-lg md:text-xl"></i>
            </button>
          )}

          <div className="relative max-w-5xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 flex items-center gap-1.5 text-background-50/60 hover:text-background-50 transition-colors cursor-pointer"
            >
              <span className="font-label text-xs tracking-wide">Esc</span>
              <i className="ri-close-line text-xl"></i>
            </button>

            <img
              src={images[lightboxIndex].src}
              alt={images[lightboxIndex].alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
              decoding="async"
            />
            <p className="text-center font-body text-sm text-background-50/70 mt-3">
              {images[lightboxIndex].caption}
              <span className="text-background-50/30 ml-2">{lightboxIndex + 1} / {images.length}</span>
            </p>
          </div>

          {lightboxIndex < images.length - 1 && (
            <button
              onClick={goNext}
              className="lightbox-arrow right-4 md:right-6 z-10"
              aria-label="Next image"
            >
              <i className="ri-arrow-right-line text-lg md:text-xl"></i>
            </button>
          )}
        </div>
      )}

      {/* ── Gallery Grid ── */}
      <section className="py-14 md:py-24 gradient-section film-grain section-popout">
        <div className="section-container">
          {/* Hero image — full width */}
          <div className="mb-4 md:mb-6">
            <div
              onClick={() => openLightbox(0)}
              className="relative aspect-[21/9] rounded-2xl overflow-hidden cursor-pointer group film-grain popout-card"
              style={{ background: 'linear-gradient(180deg, oklch(0.85 0.010 80), oklch(0.78 0.008 80))' }}
            >
              <img
                src={images[0].src}
                alt={images[0].alt}
                className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${loadedImages.has(0) ? 'opacity-100' : 'opacity-0'}`}
                style={{ objectPosition: '50% 78%', transition: 'opacity 0.6s ease-out, transform 0.7s ease-out' }}
                decoding="async"
                onLoad={() => handleImageLoad(0)}
              />
              <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/30 transition-all duration-500"></div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                  <i className="ri-zoom-in-line text-xl text-foreground-900"></i>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                <p className="font-heading text-base md:text-lg text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {images[0].caption}
                </p>
              </div>
            </div>
          </div>

          {/* 2-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {images.slice(1).map((img, i) => {
              const idx = i + 1;
              return (
                <div
                  key={img.id}
                  onClick={() => openLightbox(idx)}
                  className="relative aspect-[4/5] rounded-2xl overflow-hidden cursor-pointer group film-grain popout-card"
                  style={{ background: 'linear-gradient(180deg, oklch(0.85 0.010 80), oklch(0.78 0.008 80))' }}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className={`w-full h-full object-cover object-top transition-all duration-700 group-hover:scale-105 ${loadedImages.has(idx) ? 'opacity-100' : 'opacity-0'}`}
                    style={{ transition: 'opacity 0.6s ease-out, transform 0.7s ease-out' }}
                    decoding="async"
                    onLoad={() => handleImageLoad(idx)}
                  />
                  <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/30 transition-all duration-500"></div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                      <i className="ri-zoom-in-line text-xl text-foreground-900"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                    <p className="font-heading text-base md:text-lg text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      {img.caption}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Expansion Note ── */}
      <section className="py-12 md:py-16 section-dark film-grain">
        <div className="section-container text-center scroll-animate">
          <p className="font-body text-sm text-white/80 max-w-md mx-auto">
            This gallery is built to grow. As more photos and performances are captured, they will be added here.
          </p>
        </div>
      </section>
    </main>
    </>
  );
}